import type { OrganisationResponse } from "@finn-no/mobility-dealer-types";
import { createContext, type FC, type ReactNode, useContext } from "react";
import type { CommonDealerProps } from "../../types.ts";

type OrganisationContextValue = {
  organisation: OrganisationResponse;
  common: CommonDealerProps;
} | null;

/**
 * A standalone context for OrganisationResponse and common app props that can be provided by either:
 * - DealerItemDataProvider (item-page-layout) — automatically populated when dealer data is available
 * - OrganisationDataProvider (dealer-branding-layout) — populated directly
 *
 * This allows shared components that only need organisation data to work in both apps.
 */
export const OrganisationContext = createContext<OrganisationContextValue>(null);

type OrganisationDataProviderProps = {
  organisationResult: OrganisationResponse;
  external: CommonDealerProps;
  children: ReactNode;
};

/**
 * Provides OrganisationResponse directly. Use this in apps where the organisation data
 * is available without going through DealerItemResult (e.g. dealer-branding-layout).
 */
export function OrganisationDataProvider({ organisationResult, external, children }: OrganisationDataProviderProps) {
  return (
    <OrganisationContext value={{ organisation: organisationResult, common: external }}>{children}</OrganisationContext>
  );
}

/**
 * Returns OrganisationResponse and common external props from whichever provider is available.
 * Throws if used outside both DealerDataProvider and OrganisationDataProvider.
 */
export function useOrganisationData(): { organisationData: OrganisationResponse; external: CommonDealerProps } {
  const ctx = useContext(OrganisationContext);
  if (ctx === null) {
    throw new Error(
      "useOrganisationData: OrganisationResponse is not available. Ensure you are within a DealerItemDataProvider (with status 'ok') or an OrganisationDataProvider.",
    );
  }
  return { organisationData: ctx.organisation, external: ctx.common };
}

/**
 * Data guard HOC that renders the component only when OrganisationResponse is available
 * from either DealerDataProvider or OrganisationDataProvider.
 */
export const withOrganisationDataGuard = <Props,>(Component: FC<Props>): FC<Props> => {
  return (props) => {
    const ctx = useContext(OrganisationContext);
    if (ctx === null) {
      return null;
    }
    return Component(props);
  };
};
